import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import type { HasChildren } from '../types/components';
import { breakpoints } from '../common/breakpoints';

export type Breakpoint = 'mobile' | 'tablet' | 'notDesktop' | 'desktop';
export type HeightBreakpoint = 'short' | 'tall';

interface ReadonlySize {
  readonly width: number;
  readonly height: number;
  readonly breakpoint: Breakpoint;
  readonly heightBreakpoint: HeightBreakpoint;
}

const ViewportContext = createContext<ReadonlySize>({
  height: 0,
  width: 0,
  breakpoint: 'desktop',
  heightBreakpoint: 'tall',
});

const useViewportSizeMonitor = (): ReadonlySize => {
  const calculateBreakpointForWidth = (width: number) => {
    if (width <= breakpoints.width.small) {
      return 'mobile';
    }
    if (width > breakpoints.width.small && width < breakpoints.width.large) {
      return 'tablet';
    }
    if (width <= breakpoints.width.large) {
      return 'notDesktop';
    }
    return 'desktop';
  };

  const calculateBreakpointForHeight = (height: number) => {
    if (height <= breakpoints.height.small) {
      return 'short';
    }
    return 'tall';
  };

  const [size, setSize] = useState<ReadonlySize>({
    width: window.innerWidth,
    height: window.innerHeight,
    breakpoint: calculateBreakpointForWidth(window.innerWidth),
    heightBreakpoint: calculateBreakpointForHeight(window.innerHeight),
  });

  const handleResize = useCallback(() => {
    setSize({
      width: window.innerWidth,
      height: window.innerHeight,
      breakpoint: calculateBreakpointForWidth(window.innerWidth),
      heightBreakpoint: calculateBreakpointForHeight(window.innerHeight),
    });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return size;
};

/**
 * Measures the inner width and height of the window element and makes the dimensions available to
 * the {@link useViewport} hook through context.
 */
export function ViewportProvider({ children }: HasChildren) {
  const size = useViewportSizeMonitor();
  return <ViewportContext.Provider value={size}>{children}</ViewportContext.Provider>;
}

/**
 * @returns The inner width and height of the window element, representing the dimensions of the
 * user's viewport for use in responsive layouts.
 */
export const useViewport = (): ReadonlySize => useContext(ViewportContext);
